import axios, { AxiosRequestConfig } from "axios";
import store from "../Store"; // Import the Redux store
import { clearToken, setToken } from "./../Slice/Auth/AuthSlice";
import { refreshAccessToken } from "./../ApiCalls/Auth/refreshToken"; // Assume you have a thunk for refreshing the token
import { getHostUrl } from "./getHostUrl";
import { showErrorToast } from "../../components/atoms/Utlis/Toast";
import { getWhiteLableObject } from "../../config/whitelable";
import { getStoredStudioAccess } from "../../Utils/permissionUtils";

const api = axios.create({
  baseURL: `${getHostUrl()}`,
});
export const getTokens = () => {
  const state = store.getState();
  let accessToken = state.auth.access_token;
  if (!accessToken || accessToken === "null" || accessToken === "undefined") {
    accessToken = localStorage.getItem("access_token");
  }
  let refreshToken = state.auth.refresh_token;
  if (!refreshToken || refreshToken === "null" || refreshToken === "undefined") {
    refreshToken = localStorage.getItem("refresh_token");
  }

  if (accessToken === "null" || accessToken === "undefined") accessToken = null;
  if (refreshToken === "null" || refreshToken === "undefined") refreshToken = null;

  return { accessToken, refreshToken };
};

let last403ToastTime = 0;
let last403ToastMessage = "";
let isLoggingOutDueToRevocation = false;

const showThrottled403Toast = (message: string) => {
  const now = Date.now();
  // Don't repeat identical toast within 4s, or any 403 toast within 1.5s
  if (
    now - last403ToastTime < 1500 ||
    (message === last403ToastMessage && now - last403ToastTime < 4000)
  ) {
    return;
  }
  last403ToastTime = now;
  last403ToastMessage = message;
  showErrorToast(message);
};

// Generic function to handle API calls with token refresh
const apiCall = async (
  options: AxiosRequestConfig<any>,
  accessToken = getTokens().accessToken,
  refreshToken = getTokens().refreshToken
) => {
  // Attach the access token to the request
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    // "ngrok-skip-browser-warning": true,
    ...options.headers,
  };

  try {
    // // console.log(headers);
    // // console.log(options);
    const response = await api({
      ...options,
      headers: headers,
    });
    return response.data;
  } catch (error: any) {
    // console.log(error)
    // console.log("error above sending refresh token api");
    // console.log(error.response);
    if (
      error &&
      error.response &&
      error.response.status === 403
    ) {
      const state = store.getState();
      const user = state.auth.user;
      const storedAccess = getStoredStudioAccess();
      const isStudioOwner =
        user?.role === 3 ||
        state.role?.myPermissions?.is_studio_owner === true ||
        storedAccess?.is_studio_owner === true;
      const isSubUser =
        user?.role === 4 ||
        (!isStudioOwner && (!!user || !!localStorage.getItem("access_token")));

      const required =
        error.response.data?.error?.required_permission ||
        error.response.data?.required_permission;
      const url = String(error.config?.url || "");
      const isPermissionsEndpoint = url.includes("/account/user/me/permissions/");
      const responseMsg = String(
        error.response.data?.message || error.response.data?.error?.message || ""
      );
      const isMembershipDenied =
        responseMsg.toLowerCase().includes("membership not found") ||
        responseMsg.toLowerCase().includes("role has been removed");
      const isRoleRevokedForSubUser =
        isSubUser && (isPermissionsEndpoint || isMembershipDenied);

      if (isRoleRevokedForSubUser) {
        if (!isLoggingOutDueToRevocation) {
          isLoggingOutDueToRevocation = true;
          showErrorToast("Your role has been removed. You have been logged out.");
          store.dispatch(clearToken());
          if (!window.location.pathname.startsWith("/auth")) {
            window.location.href = "/auth/login";
          }
          setTimeout(() => {
            isLoggingOutDueToRevocation = false;
          }, 3000);
        }
        throw error;
      }

      const msg = required
        ? `Permission denied: Requires ${required}`
        : (error.response.data?.message || "Permission denied. You don't have access to this feature.");
      showThrottled403Toast(msg);
      throw error;
    } else if (
      error &&
      error.response &&
      error.response.status === 401 &&
      refreshToken
    ) {
      // Token might be expired, attempt to refresh it
      try {
        const newAccessToken = await store
          .dispatch(refreshAccessToken(refreshToken))
          .unwrap();
        // console.log(newAccessToken);
        // Update the access token in the Redux store
        store.dispatch(
          setToken({
            access_token: newAccessToken.data,
            refresh_token: refreshToken,
          })
        );
        if (localStorage.getItem("access_token")) {
          localStorage.setItem("access_token", newAccessToken.data);
        }
        // Retry the original request with the new access token
        const retryHeaders = {
          Authorization: `Bearer ${newAccessToken.data}`,
          ...options.headers,
        };

        const retryResponse = await api({
          ...options,
          headers: retryHeaders,
        });
        return retryResponse.data;
      } catch (refreshError) {
        // console.log("error in refresh token request from authAPI")
        console.log(refreshError);
        // If refresh fails, clear tokens and handle error appropriately;
        store.dispatch(clearToken());
        throw refreshError;
      }
    } else {
      throw error;
    }
  }
};

export default apiCall;
